import type { AppDispatch } from "../../app/store";
import { refreshAccess, setUnauthorized } from "../../features/auth/authSlice";
import { ISocketManager } from "../ISocketManager";
import { isSessionInvalidStatus } from "../../features/auth/authService";
let wakeListenersInstalled = false;

const refresh_security_time = 60_000; // 60 seconds
let refreshTimer: number | undefined;

const needsRefreshNow = (expMs: number) => {
  // refresh if expired OR within the security window
  return Date.now() >= expMs - refresh_security_time;
};

const refreshNow = async (
  socketsToRefresh: ISocketManager[],
  dispatch: AppDispatch,
) => {
  await dispatch(refreshAccess()).unwrap();
  socketsToRefresh.forEach((s) => s.handleTokenRefresh());
};

// Only an invalid session logs out. Everything else - offline (no status),
// rate limited (429), server restarting (5xx) - is retried later.
export const shouldRetry = (err: any): boolean =>
  !isSessionInvalidStatus(err?.status);

const parseExpMs = (jwt: string): number | null => {
  try {
    const payload = JSON.parse(atob(jwt.split(".")[1])) as { exp?: number };
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

/** The stored access token is missing, unreadable or (about to be) expired */
export const storedTokenNeedsRefresh = () => {
  const access = localStorage.getItem("daex-auth-token");
  const expMs = access ? parseExpMs(access) : null;
  return !expMs || needsRefreshNow(expMs);
};

let currentSockets: ISocketManager[] = [];

/**
 * Master function that both schedules and installs wake listeners.
 */
export const scheduleSilentRefresh = async (
  socketsToRefresh: ISocketManager[],
  installWakeListeners = true,
  dispatch: AppDispatch,
) => {
  currentSockets = socketsToRefresh; // immer aktuell halten

  const access = localStorage.getItem("daex-auth-token");
  if (!access) return;

  const expMs = parseExpMs(access);
  if (!expMs) {
    dispatch(setUnauthorized());
    return;
  }

  // ✅ if we're already late (common after background), refresh immediately
  if (needsRefreshNow(expMs)) {
    try {
      await refreshNow(socketsToRefresh, dispatch);
    } catch (err: any) {
      if (shouldRetry(err)) {
        refreshTimer = window.setTimeout(
          () => scheduleSilentRefresh(socketsToRefresh, false, dispatch),
          15_000,
        );
        return;
      }
      return void dispatch(setUnauthorized());
    }
  }

  const msUntilRefresh = expMs - Date.now() - refresh_security_time;

  if (refreshTimer) window.clearTimeout(refreshTimer);

  const doRefresh = async () => {
    try {
      await dispatch(refreshAccess()).unwrap();
      socketsToRefresh.forEach((s) => s.handleTokenRefresh());
    } catch (err: any) {
      if (shouldRetry(err)) {
        refreshTimer = window.setTimeout(
          () => scheduleSilentRefresh(socketsToRefresh, false, dispatch),
          15_000,
        );
        return;
      }
      dispatch(setUnauthorized());
      return;
    }

    // reschedule based on the new token
    scheduleSilentRefresh(socketsToRefresh, false, dispatch);
  };

  const delay = Math.max(msUntilRefresh, 5_000); // avoid tight loops
  refreshTimer = window.setTimeout(doRefresh, delay);

  if (installWakeListeners && !wakeListenersInstalled) {
    installWakeRefresh(socketsToRefresh, dispatch);
    wakeListenersInstalled = true;
  }
};

/**
 * Re-runs the scheduler whenever the tab wakes or gains focus.
 */
export const installWakeRefresh = (
  _socketsToRefresh: ISocketManager[],
  dispatch: AppDispatch,
) => {
  const wake = () => {
    // iOS drops sockets in the background - reconnect before the next emit
    currentSockets.forEach((s) => s.ensureConnected());
    scheduleSilentRefresh(currentSockets, true, dispatch);
  };

  window.addEventListener("visibilitychange", () => {
    if (!document.hidden) wake();
  });
  window.addEventListener("focus", wake);
  window.addEventListener("pageshow", wake);

  // Von der nativen Hülle (DaexWebView) per injectJavaScript aufgerufen —
  // notwendig, weil visibilitychange in WKWebView beim App-Backgrounding
  // unzuverlässig feuert (bekannter WebKit-Quirk, kein Vite/RN-Problem).
  (window as any).__daexNativeResume = wake;
};
